import { labelExamples, type BusinessLabels, type UpdateBusinessLabelsRequest } from "@teamna/shared";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  BuildingIcon,
  Button,
  CellField,
  ChevronRightIcon,
  EmptyState,
  ErrorState,
  FormRow,
  FormSection,
  InlineError,
  SignInMethodsSection,
  SkeletonList,
  TagIcon,
  Tabs,
  ToggleRow,
  panelId,
  showToast,
  tabId,
} from "../components/index.ts";
import { useLabels, type LabelConcept } from "@teamna/web-client/hooks/use-labels.ts";
import { useMe } from "@teamna/web-client/hooks/use-member.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateLabels } from "@teamna/web-client/hooks/use-owner-settings.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { MyTeamPhoto } from "../photos/MyTeamPhoto.tsx";
import { BrandTab } from "./brand/brand-tab.tsx";
import { BusinessShapeSection } from "./business-shape.tsx";
import { HealthSettings } from "./health-settings.tsx";
import { CheckInSection } from "./check-in-settings.tsx";
import { FirstClassWelcomeSection } from "./messages-settings.tsx";
import { FrontDeskSettings } from "./front-desk-settings.tsx";
import { ModulesSettings } from "./modules-settings.tsx";
import { OfficePage } from "./office.tsx";
import { BusinessForm } from "./setup/business.tsx";
import { SaveBar } from "./setup/frame.tsx";

/**
 * Settings (web-app-v2.md slice E), in tabs.
 *
 * - Business: name, sport, venue and days off, the same form as setup step 1, then how the
 *   business works (who books, how classes fill: `business-shape.tsx`), then the booking rule for
 *   children (own group only, or any group), and where clients pick each class, the check-in rule
 *   (`check-in-settings.tsx`).
 * - Words: the five words the business uses for its people and its groups, singular and plural,
 *   with a sentence that uses them as the owner types. Saved with `PATCH /labels`; every screen
 *   reads its words through `useLabels()`, so the sidebar changes the moment the save lands.
 * - Health: the health questions and the terms new people answer when they join (C2,
 *   `health-settings.tsx`). Off by default.
 * - Messages: the welcome before the first class (C3, `messages-settings.tsx`), owner and admin.
 * - Brand: the business logo and the colour of the Teamna app (owner and admin), see `brand/brand-tab.tsx`.
 * - Modules: the parts of Teamna the owner or an admin turns on or off, all off by default
 *   (`modules-settings.tsx`).
 * - Front desk: what the Front desk role may see and do, with a live preview (owner and admin, D65),
 *   see `front-desk-settings.tsx`.
 * - Sign-in methods: this account's phone and email, the same section as the member profile.
 *
 * Above the tabs, two pages that open from Settings since the owner menu revamp (2026-10-09):
 * Card payments (`/owner/setup/get-paid`, "Get paid") and the Teamna plan (`/owner/billing`). They
 * keep their own addresses, because Stripe sends the owner back to both. Control Center has no
 * Teamna plan page, so it turns that link off.
 */

type Tab = "business" | "words" | "modules" | "health" | "messages" | "brand" | "front-desk" | "photo" | "sign-in";

export function OwnerSettingsScreen({ planLink = true }: { planLink?: boolean }) {
  const [tab, setTab] = useState<Tab>("business");
  const me = useMe();
  const { label } = useLabels();

  return (
    <OfficePage title="Settings" description="Your business, how you get paid, the words it uses, and how you sign in.">
      {(business) => (
        <div>
          <ul className="mb-6 grid gap-3 sm:grid-cols-2">
            <SettingsLink
              to="/owner/setup/get-paid"
              icon={BuildingIcon}
              title="Card payments"
              text={
                business.cardPaymentsComingSoon
                  ? `Coming soon. ${label("member", { plural: true, capital: true })} will pay by card and with payment links.`
                  : `Connect your business to Stripe, so ${label("member", { plural: true })} pay by card and with payment links.`
              }
            />
            {planLink ? (
              <SettingsLink to="/owner/billing" icon={TagIcon} title="Teamna plan" text="Your plan, your card and your invoices." />
            ) : null}
          </ul>
          <Tabs
            label="Settings"
            tabs={[
              { id: "business", label: "Business" },
              { id: "words", label: "Words" },
              { id: "modules", label: "Modules" },
              { id: "health", label: "Health" },
              { id: "messages", label: "Messages" },
              { id: "brand", label: "Brand" },
              { id: "front-desk", label: "Front desk" },
              { id: "photo", label: "Your photo" },
              { id: "sign-in", label: "Sign-in methods" },
            ]}
            selectedId={tab}
            onSelect={(id) => setTab(id as Tab)}
          />
          <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
            {tab === "business" ? (
              business.staffRole === "owner" ? (
                <div className="flex flex-col gap-6">
                  <BusinessForm businessId={business.businessId} />
                  <BusinessShapeSection businessId={business.businessId} />
                  <BookingRule businessId={business.businessId} />
                  <CheckInSection businessId={business.businessId} />
                </div>
              ) : (
                <EmptyState title="Owner only" message="Ask the owner to change the business details." />
              )
            ) : tab === "words" ? (
              business.staffRole === "owner" || business.staffRole === "admin" ? (
                <WordsTab businessId={business.businessId} />
              ) : (
                <EmptyState title="Owner and admin only" message="Ask the owner to change the words." />
              )
            ) : tab === "modules" ? (
              business.staffRole === "owner" || business.staffRole === "admin" ? (
                <ModulesSettings businessId={business.businessId} />
              ) : (
                <EmptyState title="Owner and admin only" message="Ask the owner to turn modules on or off." />
              )
            ) : tab === "health" ? (
              business.staffRole === "owner" || business.staffRole === "admin" ? (
                <HealthSettings businessId={business.businessId} />
              ) : (
                <EmptyState title="Owner and admin only" message="Ask the owner to change the health questions." />
              )
            ) : tab === "messages" ? (
              business.staffRole === "owner" || business.staffRole === "admin" ? (
                <FirstClassWelcomeSection businessId={business.businessId} />
              ) : (
                <EmptyState title="Owner and admin only" message="Ask the owner to change the messages." />
              )
            ) : tab === "brand" ? (
              business.staffRole === "owner" || business.staffRole === "admin" ? (
                <BrandTab business={business} />
              ) : (
                <EmptyState title="Owner and admin only" message="Ask the owner to change the logo." />
              )
            ) : tab === "front-desk" ? (
              business.staffRole === "owner" || business.staffRole === "admin" ? (
                <FrontDeskSettings businessId={business.businessId} />
              ) : (
                <EmptyState title="Owner and admin only" message="Ask the owner to change what the front desk can do." />
              )
            ) : tab === "photo" ? (
              <FormSection
                title="Your photo"
                description="Your face shows next to your name in Teamna. This business keeps its own photo of you."
              >
                <div className="py-4">
                  <MyTeamPhoto business={business} />
                </div>
              </FormSection>
            ) : me.data ? (
              <SignInMethodsSection user={me.data.user} />
            ) : me.error ? (
              <ErrorState error={me.error} onRetry={() => void me.refetch()} />
            ) : (
              <SkeletonList rows={2} />
            )}
          </div>
        </div>
      )}
    </OfficePage>
  );
}

// --- booking rule ------------------------------------------------------------

/**
 * "Each participant books only their own group" (the `allowAnyGroup` setting, read the other way
 * round). On by default where classes fill as fixed groups (`class_fill`): the business places each
 * participant in a group, and the member's Book tab then shows only that group's sessions. The
 * words are the business's labels, so the text names no child (F2): a studio where clients book for
 * themselves reads it as well as a family business.
 */
function BookingRule({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  if (!business.data) return null;
  const ownOnly = !business.data.settings.allowAnyGroup;
  const withoutGroup = business.data.settings.allowBookingWithoutGroup;
  const participant = label("participant");
  const participants = label("participant", { plural: true, capital: true });
  return (
    <FormSection title="Booking" description={`Which sessions ${label("member", { plural: true })} can book.`}>
      <ToggleRow
        label={`Each ${participant} books only their own ${label("group")}`}
        hint={`On: the Book tab shows each ${participant} only the sessions of their own ${label("group")}. You can still book any session for them yourself.`}
        checked={ownOnly}
        disabled={update.isPending}
        onChange={(next) =>
          update.mutate(
            { settings: { allowAnyGroup: !next } },
            { onSuccess: () => showToast(next ? `Only their own ${label("group")} from now on.` : `Any ${label("group")} from now on.`) },
          )
        }
      />
      {ownOnly ? (
        <ToggleRow
          label={`A ${participant} with no ${label("group")} yet can book`}
          hint={
            withoutGroup
              ? `On: until you place a ${participant}, they can book any session.`
              : `Off: a ${participant} sees no sessions until you put them in a ${label("group")}.`
          }
          checked={withoutGroup}
          disabled={update.isPending}
          onChange={(next) =>
            update.mutate(
              { settings: { allowBookingWithoutGroup: next } },
              {
                onSuccess: () =>
                  showToast(next ? `${participants} with no ${label("group")} can book any session.` : `${participants} with no ${label("group")} see no sessions until you place them.`),
              },
            )
          }
        />
      ) : null}
      {update.error ? <InlineError error={update.error} /> : null}
    </FormSection>
  );
}

// --- words -------------------------------------------------------------------

type Pair = { singular: string; plural: string };
type Words = Record<LabelConcept, Pair>;

const CONCEPTS: Array<{ concept: LabelConcept; title: string; hint: string }> = [
  { concept: "business", title: "Your organisation", hint: "For example business, club, academy or studio." },
  { concept: "staff", title: "People who run sessions", hint: `For example ${labelExamples("staff")}.` },
  { concept: "member", title: "People who book and pay", hint: `For example ${labelExamples("member")}.` },
  { concept: "participant", title: "People who attend", hint: `For example ${labelExamples("participant")}.` },
  { concept: "group", title: "A class or team", hint: `For example ${labelExamples("group", 4)}.` },
];

const WORD = /^[a-zA-Z ]{1,24}$/;

function wordsFrom(labels: BusinessLabels): Words {
  const pair = (concept: LabelConcept): Pair => ({
    singular: labels[`${concept}Singular` as keyof BusinessLabels],
    plural: labels[`${concept}Plural` as keyof BusinessLabels],
  });
  return {
    business: pair("business"),
    staff: pair("staff"),
    member: pair("member"),
    participant: pair("participant"),
    group: pair("group"),
  };
}

function capital(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}

/** The sentence under the words, so an owner sees them in use before saving. */
export function exampleSentence(words: Words): string {
  const w = (concept: LabelConcept, plural = false) => {
    const pair = words[concept];
    return (plural ? pair.plural : pair.singular).trim() || "...";
  };
  return (
    `${capital(w("member"))} Sara books a place for each ${w("participant")}: 2 ${w("participant", true)} ` +
    `in the Tuesday ${w("group")}. The ${w("staff")} sees them on the list. ` +
    `Your ${w("business")} has 4 ${w("group", true)}, 3 ${w("staff", true)} and 40 ${w("member", true)}.`
  );
}

function WordsTab({ businessId }: { businessId: string }) {
  const business = useOwnerBusiness(businessId);
  if (business.isLoading) return <SkeletonList rows={3} />;
  if (business.error || !business.data) {
    return <ErrorState error={business.error ?? new Error("missing")} onRetry={() => void business.refetch()} />;
  }
  return <WordsForm key={JSON.stringify(business.data.labels)} businessId={businessId} labels={business.data.labels} />;
}

function WordsForm({ businessId, labels }: { businessId: string; labels: BusinessLabels }) {
  const saved = wordsFrom(labels);
  const [words, setWords] = useState<Words>(saved);
  const update = useUpdateLabels(businessId);
  const { label } = useLabels();

  const changed = CONCEPTS.filter(
    ({ concept }) =>
      words[concept].singular.trim() !== saved[concept].singular ||
      words[concept].plural.trim() !== saved[concept].plural,
  ).map(({ concept }) => concept);
  const invalid = CONCEPTS.filter(
    ({ concept }) => !WORD.test(words[concept].singular.trim()) || !WORD.test(words[concept].plural.trim()),
  ).map(({ concept }) => concept);

  function set(concept: LabelConcept, patch: Partial<Pair>) {
    setWords((current) => ({ ...current, [concept]: { ...current[concept], ...patch } }));
  }

  function save() {
    if (changed.length === 0 || invalid.length > 0) return;
    const body: UpdateBusinessLabelsRequest = {};
    for (const concept of changed) {
      body[concept] = {
        singular: words[concept].singular.trim(),
        plural: words[concept].plural.trim(),
      };
    }
    update.mutate(body, { onSuccess: () => showToast("Words saved") });
  }

  return (
    <div className="flex flex-col gap-4">
      <FormSection
        title="Words"
        description={`The words your ${label("business")} uses on every screen and in every message. Only the words change, nothing else.`}
      >
        {CONCEPTS.map(({ concept, title, hint }) => {
          const bad = invalid.includes(concept);
          return (
            <FormRow key={concept} label={title} hint={hint}>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1 text-[13px] font-semibold text-ink-2">
                  <span aria-hidden="true">One</span>
                  <CellField
                    label={`${title}, one`}
                    value={words[concept].singular}
                    maxLength={24}
                    aria-invalid={bad && !WORD.test(words[concept].singular.trim()) ? true : undefined}
                    onChange={(event) => set(concept, { singular: event.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1 text-[13px] font-semibold text-ink-2">
                  <span aria-hidden="true">More than one</span>
                  <CellField
                    label={`${title}, more than one`}
                    value={words[concept].plural}
                    maxLength={24}
                    aria-invalid={bad && !WORD.test(words[concept].plural.trim()) ? true : undefined}
                    onChange={(event) => set(concept, { plural: event.target.value })}
                  />
                </div>
              </div>
              {bad ? (
                <p className="mt-1.5 text-[13px] font-semibold text-cancelled">
                  Use letters and spaces only, up to 24.
                </p>
              ) : null}
            </FormRow>
          );
        })}
        <FormRow label="How it reads">
          <p
            aria-live="polite"
            data-testid="words-example"
            className="rounded-xl bg-accent-surface px-4 py-3 text-[15px] text-accent-strong"
          >
            {exampleSentence(words)}
          </p>
        </FormRow>
      </FormSection>

      <SaveBar note={changed.length === 0 ? "No changes yet." : undefined}>
        <Button disabled={changed.length === 0 || invalid.length > 0} loading={update.isPending} onClick={save}>
          Save words
        </Button>
        {changed.length > 0 ? (
          <Button variant="secondary" onClick={() => setWords(saved)}>
            Undo changes
          </Button>
        ) : null}
      </SaveBar>
      {update.error ? <InlineError error={update.error} /> : null}
    </div>
  );
}

/** A page that opens from Settings, as a card: the same look as the Tools cards. */
function SettingsLink({
  to,
  icon: Icon,
  title,
  text,
}: {
  to: string;
  icon: typeof BuildingIcon;
  title: string;
  text: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex h-full items-start gap-3 rounded-card border border-line bg-paper p-4 outline-none transition-colors hover:bg-surface focus-visible:ring-4 focus-visible:ring-accent-soft"
      >
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-accent-surface text-accent-strong">
          <Icon size={22} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] font-semibold text-ink">{title}</span>
          <span className="mt-1 block text-[14px] text-ink-2">{text}</span>
        </span>
        <ChevronRightIcon size={20} className="mt-2 shrink-0 text-ink-3" />
      </Link>
    </li>
  );
}
